package com.shadowings.gero.domain.repository

import com.shadowings.gero.domain.model.ThemeMode
import com.shadowings.gero.domain.model.UserData
import kotlinx.coroutines.flow.Flow

interface UserDataRepository {
    val userData: Flow<UserData>
    // Synchronous snapshot of userData for a ViewModel's initial state; null until DataStore has
    // loaded. Use userData.first() whenever a decision depends on the value.
    val currentUserDataOrNull: UserData?
    val testMode: Flow<Boolean>
    val showCheats: Flow<Boolean>
    val themeMode: Flow<ThemeMode>
    val soundEnabled: Flow<Boolean>
    val hapticEnabled: Flow<Boolean>
    val hideOptions: Flow<Boolean>
    val reminderEnabled: Flow<Boolean>
    val reminderHour: Flow<Int>
    val reminderMinute: Flow<Int>
    val calendarPromptDone: Flow<Boolean>
    val calendarPromptDismissedAt: Flow<Long>

    // --- Operazioni CRUD atomiche ---
    suspend fun setLanguage(language: String)
    suspend fun setThemeMode(themeMode: ThemeMode)
    suspend fun setSoundEnabled(enabled: Boolean)
    suspend fun setHapticEnabled(enabled: Boolean)
    suspend fun setHideOptions(enabled: Boolean)
    suspend fun setStar(id: String, stars: Int)
    suspend fun setLessonCompleted(lessonId: String)
    suspend fun setTutorialCompleted()
    suspend fun setTestMode(enabled: Boolean)
    suspend fun setShowCheats(enabled: Boolean)
    suspend fun setAlynColor(categoryKey: String, index: Int)
    suspend fun setNiloColor(categoryKey: String, index: Int)
    suspend fun setZetaColor(categoryKey: String, index: Int)
    suspend fun setMaskoColor(categoryKey: String, index: Int)
    suspend fun addStars(amount: Int = 1)
    suspend fun addExperience(amount: Int)
    suspend fun setHearts(amount: Int)
    suspend fun addHearts(amount: Int)
    suspend fun setLastOpenDate(date: Int)
    suspend fun setStrike(strike: Int)
    suspend fun setLastQuizDate(date: Int)
    suspend fun recordQuizCompletedDate(dateStr: String = com.shadowings.gero.util.currentDateAsString())
    suspend fun setReminderEnabled(enabled: Boolean)
    suspend fun setReminderTime(hour: Int, minute: Int)
    suspend fun resetAlynColors()
    suspend fun resetNiloColors()
    suspend fun resetZetaColors()
    suspend fun resetMaskoColors()
    suspend fun resetData()
    suspend fun toggleFavoriteHiero(code: String)
    suspend fun toggleFavoriteWord(id: String)
    suspend fun setWalkthroughCompleted()
    suspend fun setTabWalkthroughCompleted(tabIndex: Int)
    suspend fun setLastSeenChangelogVersion(version: String)
    suspend fun restoreUserData(userData: UserData)
    suspend fun unlockAllLessons()

    // Debug cheat: completes chapters 1-8 (stars/perfect/explanations, same as
    // unlockAllLessons) but deliberately leaves chapters 9-12 untouched, so
    // SentenceRepository's "seen words" pool for chapter 8's VSO lessons only
    // contains chapters 1-8's vocabulary - lets a dev verify chapter 8's quizzes
    // behave correctly before the later chapters' proper nouns (gods/pharaohs,
    // taught in chapter 12) are unlocked, instead of always testing against the
    // full, already-everything-unlocked vocabulary.
    suspend fun unlockLessonsUpToChapter9()
    suspend fun saveBuilderWord(word: com.shadowings.gero.domain.model.SavedBuilderWord)
    suspend fun deleteBuilderWord(id: String)
    suspend fun updateBuilderWord(word: com.shadowings.gero.domain.model.SavedBuilderWord)
    suspend fun setReviewPromptDone(done: Boolean = true)
    suspend fun setFeaturePollAnswered(pollKey: String, answered: Boolean = true)
    suspend fun setFullVersionUnlocked(unlocked: Boolean)
    suspend fun setCalendarPromptDone(done: Boolean = true)
    suspend fun setCalendarPromptDismissedAt(epochMillis: Long)

    // --- Streak Restoration ---
    // Ensures a token exists (as unused) for every level from 1 up to `level`, without touching
    // any level already present (put-if-absent per key). Idempotent and safe to call repeatedly
    // with the same or a lower level, so it doubles as both the per-level-up grant (called with
    // the new level after a level-up, including multi-level jumps in one call) and the one-time
    // retroactive backfill (called with the player's current level).
    suspend fun ensureStreakRestorationTokensUpTo(level: Int)
    // Marks one available (unused) token as used and, in the same transaction, records `dateStr`
    // as a completed quiz day (recalculating the streak) — a no-op if no token is available.
    suspend fun redeemStreakRestorationToken(dateStr: String)
    suspend fun setStreakRestorationBackfillDone(done: Boolean = true)
    suspend fun setStreakRestorationBadgeSeen(seen: Boolean = true)

    // --- Review your mistakes ---
    // Upserts a missed question by its (content-derived, see WrongAnswerRecord.buildId) `id`:
    // bumps `attempts` and refreshes `timestamp` if already present, otherwise adds it.
    suspend fun recordWrongAnswer(record: com.shadowings.gero.domain.model.WrongAnswerRecord)
    // Removes a question from the mistake list, e.g. once the user answers it correctly again.
    suspend fun removeWrongAnswer(id: String)
    // Wipes the whole mistake list.
    suspend fun clearWrongAnswers()
}
